import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { useAuth } from '../../shared/auth/AuthContext';
import type {
  ChecklistItem,
  ClosingDashboard,
  PaginatedClosingPortal,
  Conciliacao,
  DashboardQuery,
  FechamentoPeriodo,
  ModuloFechamento,
  Paginated,
  UpdateChecklistItemInput,
  UpdateConciliacaoInput,
  ValidarAssinarInput,
} from './types';

/* ═══════════════════════════════════════════════════════════════════════════
   Fechamento Mensal — Hooks (contrato real /api/v1/closing)

   Routes consumidas:
   - GET  /closing/periods/dashboard            → useClosingDashboard
   - GET  /closing/periods                      → useClosingPeriods
   - GET  /closing/periods/:id                  → useClosingPeriod
   - GET  /closing/periods/:id/modules          → useClosingModules
   - GET  /closing/periods/:id/checklist        → useClosingChecklist
   - GET  /closing/periods/:id/reconciliation   → useClosingReconciliation
   - GET  /closing/portal                       → useClosingPortal
   - POST /closing/periods/:id/validate-sign    → useValidateSign
   - PATCH /closing/periods/:id/checklist/:itemId → useUpdateChecklistItem
   - PATCH /closing/periods/:id/reconciliation/:itemId → useUpdateConciliacao
   - POST /closing/periods/:id/lock             → useTogglePeriodLock

   OBS (contrato): o DashboardQuery exige mes+ano (IsInt no DTO); o tenant vem
   da sessão (AuthGuard + TenantGuard injetam tenantId/companyIds) — as páginas
   não enviam tenantId no corpo. O useAuth é usado apenas para pré-preencher o
   nome do assinante (user.name) nas mutações de escrita.
   ═══════════════════════════════════════════════════════════════════════════ */

export const closingQueryKeys = {
  dashboard: (q?: DashboardQuery) => ['closing', 'dashboard', q] as const,
  periods: (params?: Record<string, unknown>) => ['closing', 'periods', params] as const,
  period: (id: string) => ['closing', 'periods', id] as const,
  modules: (id: string) => ['closing', 'periods', id, 'modules'] as const,
  checklist: (id: string, params?: Record<string, unknown>) =>
    ['closing', 'periods', id, 'checklist', params] as const,
  reconciliation: (id: string, params?: Record<string, unknown>) =>
    ['closing', 'periods', id, 'reconciliation', params] as const,
  portal: (params?: Record<string, unknown>) => ['closing', 'portal', params] as const,
} as const;

/* ── Dashboard Matriz Brasil ── */

export function useClosingDashboard(query: DashboardQuery | null) {
  return useQuery({
    queryKey: closingQueryKeys.dashboard(query ?? undefined),
    queryFn: () =>
      apiClient.get<ClosingDashboard>('/closing/periods/dashboard', {
        params: query ? { mes: String(query.mes), ano: String(query.ano) } : undefined,
      }),
    enabled: !!query,
  });
}

/* ── Lista de períodos (grid auxiliar / navegação) ── */

export function useClosingPeriods(params?: { status?: string; ano?: number; mes?: number; page?: number; limit?: number }) {
  return useQuery({
    queryKey: closingQueryKeys.periods(params),
    queryFn: () =>
      apiClient.get<Paginated<FechamentoPeriodo>>('/closing/periods', {
        params: {
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.ano ? { ano: String(params.ano) } : {}),
          ...(params?.mes ? { mes: String(params.mes) } : {}),
          ...(params?.page ? { page: String(params.page) } : {}),
          ...(params?.limit ? { limit: String(params.limit) } : {}),
        },
      }),
  });
}

/* ── Período único (header das telas de workflow/conciliação) ── */

export function useClosingPeriod(periodId?: string) {
  return useQuery({
    queryKey: closingQueryKeys.period(periodId ?? ''),
    queryFn: () => apiClient.get<FechamentoPeriodo>(`/closing/periods/${periodId}`),
    enabled: !!periodId,
  });
}

/* ── Módulos da Matriz Brasil de um período ── */

export function useClosingModules(periodId?: string) {
  return useQuery({
    queryKey: closingQueryKeys.modules(periodId ?? ''),
    queryFn: () =>
      apiClient.get<Paginated<ModuloFechamento>>(`/closing/periods/${periodId}/modules`, {
        params: { limit: '20' },
      }),
    enabled: !!periodId,
  });
}

/* ── Checklist do workflow (5 etapas) ── */

export function useClosingChecklist(
  periodId?: string,
  params?: { etapa?: string; status?: string; page?: number; limit?: number },
) {
  return useQuery({
    queryKey: closingQueryKeys.checklist(periodId ?? '', params),
    queryFn: () =>
      apiClient.get<Paginated<ChecklistItem>>(`/closing/periods/${periodId}/checklist`, {
        params: {
          ...(params?.etapa ? { etapa: params.etapa } : {}),
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.page ? { page: String(params.page) } : {}),
          ...(params?.limit ? { limit: String(params.limit) } : {}),
        },
      }),
    enabled: !!periodId,
  });
}

/* ── Conciliação fiscal × contábil ── */

export function useClosingReconciliation(
  periodId?: string,
  params?: { status?: string; accountCode?: string; page?: number; limit?: number },
) {
  return useQuery({
    queryKey: closingQueryKeys.reconciliation(periodId ?? '', params),
    queryFn: () =>
      apiClient.get<Paginated<Conciliacao>>(`/closing/periods/${periodId}/reconciliation`, {
        params: {
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.accountCode ? { accountCode: params.accountCode } : {}),
          ...(params?.page ? { page: String(params.page) } : {}),
          ...(params?.limit ? { limit: String(params.limit) } : {}),
        },
      }),
    enabled: !!periodId,
  });
}

/* ── Portal do Contador (multi-empresa) ── */

export function useClosingPortal(params?: { search?: string; statusFiscal?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: closingQueryKeys.portal(params),
    queryFn: () =>
      apiClient.get<PaginatedClosingPortal>('/closing/portal', {
        params: {
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.statusFiscal ? { statusFiscal: params.statusFiscal } : {}),
          ...(params?.page ? { page: String(params.page) } : {}),
          ...(params?.limit ? { limit: String(params.limit) } : {}),
        },
      }),
  });
}

/* ── Mutações ── */

/** Valida o workflow e assina/fecha o período (POST /closing/periods/:id/validate-sign). */
export function useValidateSign(periodId?: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (input: ValidarAssinarInput) =>
      apiClient.post<FechamentoPeriodo>(`/closing/periods/${periodId}/validate-sign`, {
        assinadoPor: input.assinadoPor || user?.name || 'Assinante',
        cargoAssinante: input.cargoAssinante,
        notaValidacao: input.notaValidacao,
        fechadoPor: input.fechadoPor ?? user?.name ?? undefined,
      }),
    onSuccess: () => {
      if (periodId) {
        qc.invalidateQueries({ queryKey: closingQueryKeys.period(periodId) });
        qc.invalidateQueries({ queryKey: closingQueryKeys.dashboard() });
        qc.invalidateQueries({ queryKey: ['closing', 'periods'] });
      }
    },
  });
}

/** Marca item do checklist (PATCH /closing/periods/:id/checklist/:itemId). */
export function useUpdateChecklistItem(periodId?: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: UpdateChecklistItemInput }) =>
      apiClient.patch<ChecklistItem>(`/closing/periods/${periodId}/checklist/${itemId}`, {
        ...input,
        por: input.por ?? user?.name ?? undefined,
      }),
    onSuccess: () => {
      if (periodId) {
        qc.invalidateQueries({ queryKey: closingQueryKeys.checklist(periodId) });
      }
    },
  });
}

/** Atualiza item da conciliação (PATCH /closing/periods/:id/reconciliation/:itemId). */
export function useUpdateConciliacao(periodId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: UpdateConciliacaoInput }) =>
      apiClient.patch<Conciliacao>(`/closing/periods/${periodId}/reconciliation/${itemId}`, input),
    onSuccess: () => {
      if (periodId) {
        qc.invalidateQueries({ queryKey: closingQueryKeys.reconciliation(periodId) });
        qc.invalidateQueries({ queryKey: closingQueryKeys.dashboard() });
      }
    },
  });
}

/** Liga/desliga Master Period Lock (POST /closing/periods/:id/lock). */
export function useTogglePeriodLock(periodId?: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: ({ ativar }: { ativar: boolean }) =>
      apiClient.post<FechamentoPeriodo>(`/closing/periods/${periodId}/lock`, {
        ativar,
        por: user?.name ?? undefined,
      }),
    onSuccess: () => {
      if (periodId) {
        qc.invalidateQueries({ queryKey: closingQueryKeys.period(periodId) });
        qc.invalidateQueries({ queryKey: closingQueryKeys.dashboard() });
        qc.invalidateQueries({ queryKey: closingQueryKeys.modules(periodId) });
      }
    },
  });
}